import { useState } from "react";
import { Tag, Trash2, Plus, ImageIcon, PauseCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100";

const src = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const fmtDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "short", year: "numeric" }) : null);

export const PromotionsView = ({ est, reload }) => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ title: "", description: "", valid_from: "", valid_until: "", image: "" });
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState(null);
  const promotions = est.promotions || [];
  const gallery = est.gallery || [];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/pro/promotions", {
        ...form,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        image: form.image || null,
      });
      toast.success(t("Promotion publiée sur ta page"));
      setForm({ title: "", description: "", valid_from: "", valid_until: "", image: "" });
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (p) => {
    const next = (p.status || "active") === "active" ? "inactive" : "active";
    setToggling(p.id);
    try {
      await api.patch(`/pro/promotions/${p.id}`, { status: next });
      toast.success(next === "active" ? "Promotion réactivée" : "Promotion mise en pause — masquée de ta page publique");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setToggling(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("Retirer cette promotion ?"))) return;
    try {
      await api.delete(`/pro/promotions/${id}`);
      toast.success(t("Promotion retirée"));
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="pro-promotions-view" className="mt-6 grid max-w-4xl gap-5 lg:grid-cols-2">
      <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
          <Tag className="h-4 w-4 text-[#2563EB]" />
          Créer une promotion
        </h2>
        <p className="mt-1 text-xs text-slate-500">{t("Visible immédiatement sur ta page publique.")}</p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <input data-testid="promo-title" required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder={t("Ex. : 2 cocktails pour 15 $ le jeudi")} className={inputClass} />
          <textarea data-testid="promo-description" rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder={t("Détails (optionnel)")} className={inputClass} />
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">{t("À partir du")}</label>
              <input data-testid="promo-valid-from" type="date" value={form.valid_from} onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">{t("Jusqu'au")}</label>
              <input data-testid="promo-valid-until" type="date" value={form.valid_until} onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))} className={inputClass} />
            </div>
          </div>
          <div>
            <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-slate-500">
              <ImageIcon className="h-3.5 w-3.5" />
              Image (depuis ta galerie, optionnel)
            </label>
            <div className="flex flex-wrap gap-2">
              {gallery.length === 0 && <p className="text-[11px] text-slate-400">{t("Ajoute d'abord des photos dans la section Photos.")}</p>}
              {gallery.map((g) => (
                <button
                  key={g}
                  type="button"
                  data-testid={`promo-image-${gallery.indexOf(g)}`}
                  onClick={() => setForm((f) => ({ ...f, image: f.image === g ? "" : g }))}
                  className={`h-14 w-14 overflow-hidden rounded-xl border-2 transition ${form.image === g ? "border-[#2563EB] ring-2 ring-blue-100" : "border-transparent opacity-70 hover:opacity-100"}`}
                >
                  <img src={src(g)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </div>
          <button type="submit" data-testid="promo-submit" disabled={busy} className="flex items-center gap-2 rounded-full bg-[#2563EB] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8] active:scale-95 disabled:opacity-60">
            <Plus className="h-4 w-4" />
            {busy ? "Publication…" : "Publier la promotion"}
          </button>
        </form>
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="text-base font-bold text-slate-900">{t("Promotions en cours")}</h2>
        <div className="mt-4 space-y-3">
          {promotions.length === 0 ? (
            <p data-testid="promo-empty" className="rounded-2xl bg-[#F8FAFC] p-6 text-center text-xs text-slate-400">
              {t("Aucune promotion active pour le moment.")}
            </p>
          ) : (
            promotions.map((p, i) => {
              const active = (p.status || "active") === "active";
              return (
                <div key={p.id} data-testid={`promo-item-${i}`} className={`flex items-start gap-3 rounded-2xl border border-slate-100 p-3.5 ${!active ? "opacity-60" : ""}`}>
                  {p.image ? (
                    <img src={src(p.image)} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" />
                  ) : (
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#EFF6FF]">
                      <Tag className="h-4 w-4 text-[#2563EB]" />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      {p.title}
                      <span data-testid={`promo-status-${i}`} className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold ${active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                        {active ? "Active" : "En pause"}
                      </span>
                    </p>
                    {p.description && <p className="mt-0.5 text-xs text-slate-500">{p.description}</p>}
                    {(p.valid_from || p.valid_until) && (
                      <p className="mt-0.5 text-[10px] text-slate-400">
                        {p.valid_from ? `Dès le ${fmtDate(p.valid_from)}` : "Dès maintenant"}
                        {p.valid_until ? ` — jusqu'au ${fmtDate(p.valid_until)}` : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col gap-1.5">
                    <button
                      type="button"
                      data-testid={`promo-toggle-${i}`}
                      onClick={() => toggle(p)}
                      disabled={toggling === p.id}
                      aria-label={active ? "Mettre en pause" : "Activer"}
                      title={active ? "Mettre en pause" : "Activer"}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition hover:border-blue-300 hover:text-[#2563EB] disabled:opacity-50"
                    >
                      {active ? <PauseCircle className="h-4 w-4" /> : <PlayCircle className="h-4 w-4" />}
                    </button>
                    <button type="button" data-testid={`promo-delete-${i}`} onClick={() => remove(p.id)} aria-label={t("Retirer")} className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition hover:border-red-300 hover:text-red-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
